import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { JwtHelperService } from '@auth0/angular-jwt';
import { GS1NotificationService } from '@gs1france/gs1-components-library';
import { TranslateService } from '@ngx-translate/core';
import { OAuthService } from 'angular-oauth2-oidc';
import { BehaviorSubject, forkJoin, Observable, of, Subject, switchMap, take, tap } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { COUNTRIES } from '../../../config/countries';
import { AccessDataFromToken } from '../models/AccessDataFromToken.model';
import { InfoPerso } from '../models/InfoPerso.model';
import { Invoice } from '../models/Invoice.model';
import { AccessData, AccountItem, ContactInfo } from '../models/accessData.model';
import { AccessDataByEmailInputModel, AccountModel, PrivilegeModel } from '../models/accessDataByEmailInput.model';
import { CompanyDetails } from '../models/compteEntreprise.model';
import { AllListLabelValue, LabelValue } from '../models/liste.model';
import { EcliSettings } from '../models/settings.model';
import { CompteEntrepriseService } from './compteEntreprise.service';
import { FormulaireDonneePersoService } from './formulaireDonneePerso.service';
import { InvoiceService } from './invoice.service';
import { ListeService } from './liste.service';
import { MandatService } from './mandat.service';
import { SettingService } from './settings.service';
import { AllSEPAMandates } from '../models/Mandate.model';

export type ListName =
  | 'Activite'
  | 'ContributionBracket'
  | 'Departements'
  | 'Job'
  | 'LegalForms'
  | 'MembershipStatuses'
  | 'NAF'
  | 'SalesChannel'
  | 'Packs'
  | 'Products'
  | 'Marketings';

@Injectable({ providedIn: 'root' })
export class StorageService {
  // Storage
  hasStorage: true;
  storage: Storage;
  itemStorageList = ['accessData', 'multiple', 'tokenGs1', 'session', 'nomComplet'];

  // State flags
  isRedirected = false; // Is redirected to sign cgu or complete information

  // Access & Authentication
  decodedTokenValue: AccessDataFromToken;
  decodedToken: BehaviorSubject<AccessDataFromToken> = new BehaviorSubject<AccessDataFromToken>(null);

  accessDataValue: AccessData;
  accessData: BehaviorSubject<AccessData> = new BehaviorSubject<AccessData>(null);

  tokenGS1Value: any;
  tokenGS1: BehaviorSubject<any> = new BehaviorSubject<any>(null);

  sessionValue: any;
  session: BehaviorSubject<any> = new BehaviorSubject<any>(null);

  multipleValue: boolean;
  multiple: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null);

  // User
  userValue: InfoPerso;
  user: Subject<InfoPerso> = new Subject<InfoPerso>();
  loadingUser = false;

  contactInfoValue: ContactInfo;
  contactInfo: BehaviorSubject<ContactInfo> = new BehaviorSubject<ContactInfo>(null);

  nomCompletValue: string;
  nomComplet: BehaviorSubject<string> = new BehaviorSubject<string>(null);

  // Company
  companyValue: CompanyDetails;
  company: Subject<CompanyDetails> = new Subject<CompanyDetails>();

  accountValue: AccountItem;
  account: BehaviorSubject<AccountItem> = new BehaviorSubject<AccountItem>(null);

  packValue: string;
  pack: BehaviorSubject<string> = new BehaviorSubject<string>(null);

  // Invoices & Mandates
  unpaidInvoicesValue: { count: number; toPay: Invoice[]; autoPay: Invoice[] };
  unpaidInvoices = new BehaviorSubject<{
    count: number;
    toPay: Invoice[];
    autoPay: Invoice[];
  }>(null);
  private loadingUnpaid = false;

  mandatValue: AllSEPAMandates;
  mandat = new BehaviorSubject<AllSEPAMandates>(null);
  private loadingMandat = false;

  // Lists & Settings
  listsValue: AllListLabelValue;
  lists: Subject<boolean> = new Subject<boolean>();
  listLoading = false;

  settingsValue: EcliSettings;
  settings: Subject<EcliSettings> = new Subject<EcliSettings>();
  settingsLoading = false;

  // Search
  searchTextValue: string;
  searchText: BehaviorSubject<string> = new BehaviorSubject<string>(null);

  constructor(
    @Inject(PLATFORM_ID) private platformId: any,
    private _compteEntrepriseService: CompteEntrepriseService,
    private _userService: FormulaireDonneePersoService,
    private _mandate: MandatService,
    private _listeService: ListeService,
    private _invoiceService: InvoiceService,
    private _notifService: GS1NotificationService,
    private _settingsService: SettingService,
    private _translate: TranslateService,
    public authService: OAuthService
  ) {
    this.storage = localStorage;
  }

  public read(key: string): any {
    if (isPlatformBrowser(this.platformId)) {
      const str = this.storage.getItem(key);
      return str ? JSON.parse(str) : null;
    } else {
      return null;
    }
  }

  public readRaw(key: string): string | null {
    if (isPlatformBrowser(this.platformId)) {
      const str = this.storage.getItem(key);
      return str;
    } else {
      return null;
    }
  }

  public write(key: string, value: any): void {
    value = value === undefined ? null : value;
    this.storage.setItem(key, JSON.stringify(value));
  }

  public writeRaw(key: string, value: string): void {
    this.storage.setItem(key, value);
  }

  resetStorage() {
    this.removeCompanyData();
    this.setAccount(null);
    this.setPack(null);
    this.setAccountInfo(null);
    this.setUser(null);
  }

  public setAccessData(value: AccessData) {
    this.accessDataValue = value;
    this.accessData.next(this.accessDataValue);
  }

  public removeAccessData() {
    this.accessDataValue = null;
    this.accessData.next(null);
  }

  public removeCompanyData() {
    this.removeAccessData();
    this.removeUnpaidInvoices();
    this.companyValue = null;
  }

  public updateUserWithName(firstName: string, lastName: string) {
    this.userValue.prenom = firstName;
    this.userValue.nom = lastName;
    this.setNomComplet(firstName, lastName);
    this.user.next(this.userValue);
  }

  public updateAccessDataWithName(firstName: string, lastName: string) {
    this.accessDataValue.contactFirstName = firstName;
    this.accessDataValue.contactLastName = lastName;
    this.setNomComplet(firstName, lastName);
    this.accessData.next(this.accessDataValue);
  }

  public setSearchText(value: string) {
    this.searchTextValue = value;
    this.searchText.next(value);
    this.write('searchText', value);
  }

  public getSearchText(): string {
    if (!this.searchTextValue) {
      this.searchTextValue = this.read('searchText');
    }
    return this.searchTextValue;
  }

  public clearSearchText() {
    this.searchTextValue = null;
    this.searchText.next(null);
    this.storage.removeItem('searchText');
  }

  public setAccountInfo(value: any): Observable<AllListLabelValue> {
    this.setSession(value);
    this.writeRaw('accountInfo', JSON.stringify(value) || '');
    return this.retrieveStaticLists();
  }

  private setSettings(settings: EcliSettings) {
    this.settingsValue = settings;
    this.settings.next(this.settingsValue);
  }

  public getSettings(): Observable<EcliSettings> {
    if (this.settingsLoading) {
      return this.settings.asObservable();
    }
    if (!this.settingsValue) {
      this.settingsLoading = true;
      return this._settingsService.getSettings().pipe(
        tap((settings) => {
          this.setSettings(settings);
          this.settingsLoading = false;
        })
      );
    }
    return new BehaviorSubject(this.settingsValue).asObservable();
  }

  retrieveStaticLists(): Observable<AllListLabelValue> {
    return this._listeService.getAllLists().pipe(
      take(1),
      tap((lists) => this.setStaticLists(lists))
    );
  }

  private setStaticLists(lists: AllListLabelValue) {
    this.listsValue = lists;
    this.lists.next(true);
  }

  setUser(user: InfoPerso) {
    this.userValue = user;
    this.user.next(this.userValue);
  }

  public getUser(): Observable<InfoPerso> {
    if (this.loadingUser) return this.user;
    if (!this.userValue) {
      this.loadingUser = true;
      return this._userService.getInfoPerso().pipe(
        tap((user) => {
          this.setUser(user);
          this.loadingUser = false;
        })
      );
    }
    return of(this.userValue);
  }

  private setAccount(account: AccountItem) {
    this.accountValue = account;
    this.account.next(this.accountValue);
    this.setSession(account);
  }

  public setSession(value: any) {
    this.sessionValue = value;
    if (!this.nomCompletValue) {
      this.setNomComplet(
        JSON.parse(this.sessionValue['contact_info']).FirstName,
        JSON.parse(this.sessionValue['contact_info']).LastName
      );
    }

    this.session.next(this.sessionValue);
  }

  public getSession() {
    return this.sessionValue;
  }

  private setMultiple(value: boolean) {
    this.multipleValue = value;
    this.multiple.next(this.multipleValue);
  }

  public getMultiple() {
    if (!this.multipleValue && this.multipleValue !== false) {
      this.getAccessData();
    }
    return this.multipleValue;
  }

  public setNomComplet(firstName: string, lastName: string) {
    this.nomCompletValue = firstName + ' ' + lastName;
    this.nomComplet.next(this.nomCompletValue);
  }

  public getNomComplet() {
    return this.nomCompletValue;
  }

  public setCompany(company: CompanyDetails) {
    this.companyValue = company;
    if (company && company.countryCode)
      this.companyValue.country = COUNTRIES.filter(
        (country: any) => country.alpha2Code.toLowerCase() === company.countryCode.toLowerCase()
      )[0].name;
    this.company.next(this.companyValue);
  }

  public getCompany(): Observable<CompanyDetails> {
    return this.companyValue ? of(this.companyValue) : this.company;
  }

  public getCompanyDisplayedSections(): Observable<{
    activateCA: boolean;
    activateSEPA: boolean;
    activateLogo: boolean;
    activatePacks: boolean;
    hasCustomerNumber: boolean;
  }> {
    return this.getCompany().pipe(
      take(1),
      map((company) => {
        if (!company) {
          throw new Error();
        }
        return {
          activateCA:
            !company.activite ||
            (!company.activite.label.includes('santé') &&
              !company.activite.label.includes('Administration') &&
              !company.activite.label.includes('bancaire')),
          activateSEPA:
            company.siren &&
            (company.countryCode?.toUpperCase() === 'FR' ||
              company.countryCode?.toUpperCase() === 'FR-TOM' ||
              company.countryCode?.toUpperCase() === 'FR-DOM'),
          activateLogo:
            company.typeCompte === 'GS1 Partenaires' && company.packId !== 'DD6B09B2-4124-4D6C-822B-F3102BC49835',
          activatePacks: company.typeCompte === 'GS1 Partenaires',
          hasCustomerNumber: company.customerNumber !== null && company.customerNumber?.trim()?.length > 0,
        };
      }),
      catchError(() =>
        of({
          activateCA: false,
          activateSEPA: false,
          activateLogo: false,
          activatePacks: false,
          hasCustomerNumber: false,
        })
      )
    );
  }

  public refreshAndGetCompany(): Observable<CompanyDetails> {
    this.companyValue = null;
    return this._compteEntrepriseService.getCompanyDetails().pipe(
      tap((company) => this.setCompany(company)),
      switchMap(() => this.getCompany())
    );
  }

  public setPack(pack: string) {
    this.packValue = pack;
    this.pack.next(this.packValue);
  }

  public getAccessData(): Observable<AccessData> {
    if (!this.accessDataValue) {
      const accessData = new AccessDataByEmailInputModel();
      accessData.account = new AccountModel();
      const token = this.getDecodedToken();
      accessData.account.companyId = token.companyId;
      accessData.account.name = token.companyName;
      if (token.privileges instanceof Array) {
        accessData.account.privileges = token.privileges.map((x) => new PrivilegeModel(x));
      } else {
        accessData.account.privileges.push(new PrivilegeModel(token.privileges));
      }
      this.setMultiple(this.decodedTokenValue['companies_nb'] > 1);
    }
    return new BehaviorSubject(this.accessDataValue).asObservable();
  }

  public getListLabelValue(name: ListName): Observable<LabelValue[]> {
    if (!this.listsValue) {
      return this.lists.pipe(
        take(1),
        map(() => this.listsValue.values[name])
      );
    } else return of(this.listsValue.values[name]);
  }

  public getAllListLabelValue(): Observable<AllListLabelValue> {
    if (!this.listsValue) {
      return this.lists.pipe(
        take(1),
        map(() => this.listsValue)
      );
    } else return of(this.listsValue);
  }

  public setDecodedToken(token: AccessDataFromToken) {
    this.decodedTokenValue = token;
    this.decodedToken.next(this.decodedTokenValue);
    this.setAccountInfo(token);
  }

  public getDecodedToken(): AccessDataFromToken {
    if (this.decodedTokenValue) {
      return new AccessDataFromToken(this.decodedTokenValue);
    } else {
      if (this.authService.hasValidAccessToken()) {
        const token = this.authService.getAccessToken();
        const jwtHelper = new JwtHelperService();
        const decodedToken = jwtHelper.decodeToken(token) as AccessDataFromToken;
        this.setDecodedToken(decodedToken);
        return decodedToken;
      }
    }
  }

  public getUnpaidInvoices(forceReload = false): Observable<{ count: number; toPay: Invoice[]; autoPay: Invoice[] }> {
    if (this.loadingUnpaid) {
      return this.unpaidInvoices.asObservable();
    }
    if (!this.unpaidInvoicesValue || forceReload) {
      this.loadingUnpaid = true;
      return forkJoin([this._invoiceService.getDetailInvoices('invoiceunpaid', 0, 2000), this.getMandat()]).pipe(
        tap(([invoices, mandat]) => {
          invoices.data.map((invoice) => {
            invoice.type = invoice.type ? this._translate.instant('Invoice.Types.' + invoice.type) : '';
            return invoice;
          });
          this.setUnpaidInvoices(
            invoices.count,
            invoices.data.filter((invoice) => invoice.inDebit !== true),
            invoices.data.filter((invoice) => invoice.inDebit === true)
          );
          this.loadingUnpaid = false;
        }),
        map(() => this.unpaidInvoicesValue),
        catchError((err) => {
          if (!(err && err.status && err.status === 404)) {
            this._notifService.addNotification("Vos factures impayées n'ont pu être récupérées", 'error');
          } else this.setUnpaidInvoices(0, [], []);
          this.loadingUnpaid = false;
          return of(this.unpaidInvoicesValue);
        })
      );
    }
    return new BehaviorSubject(this.unpaidInvoicesValue).asObservable();
  }

  setUnpaidInvoices(count: number, toPay: Invoice[], autoPay: Invoice[]) {
    this.unpaidInvoicesValue = { count, toPay, autoPay };
    this.unpaidInvoices.next(this.unpaidInvoicesValue);
  }

  removeUnpaidInvoices() {
    this.unpaidInvoicesValue = null;
    this.unpaidInvoices.next(this.unpaidInvoicesValue);
  }

  public getMandat(): Observable<AllSEPAMandates> {
    if (this.loadingMandat) {
      return this.mandat.asObservable();
    }
    if (!this.mandatValue) {
      this.loadingMandat = true;
      return this._mandate.getMandates().pipe(
        tap((mandates) => {
          this.setMandate(mandates);
          this.loadingMandat = false;
        }),
        catchError((err: any) => {
          if (!(err && err.status && err.status === 404)) {
            this._notifService.addNotification("Vos mandats de prélèvement n'ont pu être récupérés", 'error');
          } else {
            this.setMandate({ Mandates: [], CurrentMandate: null });
          }
          this.loadingMandat = false;
          return of(this.mandatValue);
        })
      );
    }
    return new BehaviorSubject(this.mandatValue).asObservable();
  }

  setMandate(mandates: AllSEPAMandates) {
    this.mandatValue = mandates;
    this.mandat.next(this.mandatValue);
  }

  public remove(item: string) {
    this.storage.removeItem(item);
  }

  public clearStorage() {
    this.itemStorageList.forEach((item) => this.storage.removeItem(item));
    this.resetStorage();
    this.storage.clear();
  }

  public isUserAdmin() {
    return this.userValue && this.userValue.roles && this.userValue.roles.includes('administrateur');
  }

  public isUserFederation(): Observable<boolean> {
    return this.getUser().pipe(
      take(1),
      map((user) => user && user.roles && user.roles.length == 1 && user.roles.includes('Fédération'))
    );
  }
}
